import { defineComponent, PropType } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";

import { IEnumResponse } from "../../../../../../resources-vue/vue-interfaces/i-enum";
import { IGetSupplyByName, IPatientSupply } from "../../../../../../resources-vue/vue-interfaces/i-get-supply";
import { ITypeaheadOptions } from "../../../../../../resources-vue/vue-interfaces/i-typeahead";
import { SupplyService } from "../../../../../../services/supply.service";
import { ToastrService } from "../../../../../../services/toastr.service";
import { EnumsService } from "../../../../../../services/enums.service";
import { ConvertToPalEvent } from "../../../ConvertToPendingPal";
import { useConvertToPendingPal } from "../../../store/useConvertToPendingPal";
import eventBus from "../../../../../../utilities/eventBus";

import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import TypeaheadInput from "../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import TableEmpty from "../../../../../../resources-vue/vue-custom-elements/TableEmpty/TableEmpty.vue";
import PatientVendor from "../PatientDME/PatientVendor.vue";
import SupplyTableRow from "./SupplyTableRow.vue";

export default defineComponent({
    components: {
        FormSection,
        PatientVendor,
        TypeaheadInput,
        TableEmpty,
        SupplyTableRow
    },
    props: {
        patientVendors: { type: Array as PropType<string[]>, default: [] },
        providerId: {
            type: String,
            default: ""
        },
        orderId: {
            type: String,
            default: null
        },
        patientStatus: {
            type: Number,
            default: null
        },
        isAttachOrderNotRequired: {
            type: Boolean,
            default: null
        },
        supplies: { type: Array as PropType<IPatientSupply[]>, default: [] }
    },
    data() {
        return {
            columns: 7,
            vendorTypes: ["Supplies"],
            showSupplyInputRow: null as boolean,
            selectionSupply: null as ITypeaheadOptions,
            savetoAdd: null as IPatientSupply,
            patientStatusEnums: [] as IEnumResponse[],
            supplyList: [] as IGetSupplyByName[],
            _supplyService: null,
            _toastrService: null,
            _enumsService: null
        };
    },
    computed: {
        ...mapState(useConvertToPendingPal, ["accountId", "application"]),
        isEditAllowed() {
            let pendingStatus = this.patientStatusEnums.find((status) => status.name.toLowerCase().includes("pending"));
            return (
                this.orderId ||
                (pendingStatus && this.patientStatus == pendingStatus.value) ||
                this.isAttachOrderNotRequired
            );
        }
    },
    watch: {
        selectionSupply(newValue: ITypeaheadOptions) {
            let supplySelected: IGetSupplyByName;
            if (newValue) {
                supplySelected = this.supplyList.find((supply) => supply.Id == newValue.value);
                if (supplySelected) {
                    this.savetoAdd = {
                        id: null,
                        supplyId: supplySelected.Id,
                        name: supplySelected.Name,
                        unitCost: supplySelected.UnitCost,
                        isFormulary: supplySelected.IsFormulary,
                        startDate: "",
                        endDate: "",
                        isRelated: null,
                        notRelatedComments: null
                    };
                    this.showSupplyInputRow = true;
                }
            } else {
                this.showSupplyInputRow = false;
            }
        }
    },
    emits: ["update:patientVendors", "update:supplies"],
    created() {
        this._supplyService = Container.instance.get(SupplyService);
        this._toastrService = Container.instance.get(ToastrService);
        this._enumsService = Container.instance.get(EnumsService);
    },
    async mounted() {
        let patientStatusEnums = await this._enumsService.getPatientStatus();
        this.patientStatusEnums = patientStatusEnums.getJson();
        if (!this.patientVendors?.length) {
            this.$emit("update:patientVendors", [null]);
        }
    },
    methods: {
        addNewVendor() {
            if (this.patientVendors?.length < 5) {
                const patientVendors = this.patientVendors.concat(null);
                this.$emit("update:patientVendors", patientVendors);
            }
        },
        updateSupplies() {
            eventBus.emit(ConvertToPalEvent.RequestNoteId);
        },
        async getSupplyList(filter: string) {
            this.supplyList = await this._supplyService.getSuppliesByName({
                term: filter,
                providerIds: [this.providerId],
                application: this.application,
                accountId: this.accountId
            });
            return this.displayPatientSupply(this.supplyList);
        },
        displayPatientSupply(getSupply: IGetSupplyByName[]): ITypeaheadOptions[] {
            return getSupply.map((value: IGetSupplyByName, index, number) => ({
                value: value.Id,
                name: value.Name
            }));
        },
        saveNewSupply(newSupply: IPatientSupply, oldSupply: IPatientSupply) {
            let existingSupply = this.supplies.find(
                (supply) =>
                    supply.supplyId == newSupply.supplyId &&
                    supply.startDate == newSupply.startDate &&
                    supply.endDate == newSupply.endDate
            );
            if (existingSupply) {
                this._toastrService.info({
                    title: `${existingSupply.name} already exists`,
                    message: `${existingSupply.name} is already assigned to patient between ${existingSupply.startDate} and ${existingSupply.endDate}.`
                });
            } else {
                this.supplies.push(newSupply);
                this.cancelNewSupply();
            }
        },
        cancelNewSupply() {
            this.savetoAdd = {
                id: null,
                supplyId: null,
                name: null,
                unitCost: null,
                isFormulary: null,
                startDate: "",
                endDate: "",
                isRelated: null,
                notRelatedComments: null
            };
            this.selectionSupply = null;
            this.showSupplyInputRow = false;
        },
        updateSupply(newSupply: IPatientSupply, oldSupply: IPatientSupply) {
            let supplyIndex = this.supplies.findIndex(
                (supply) =>
                    supply.supplyId == oldSupply.supplyId &&
                    supply.startDate == oldSupply.startDate &&
                    supply.endDate == oldSupply.endDate
            );
            if (supplyIndex > -1) {
                this.supplies[supplyIndex] = newSupply;
            }
        },
        deleteSupplyFromList(deleteSupply: IPatientSupply) {
            let supplies = this.supplies.filter(
                (supply) =>
                    !(
                        supply.supplyId == deleteSupply.supplyId &&
                        supply.startDate == deleteSupply.startDate &&
                        supply.endDate == deleteSupply.endDate
                    )
            );
            this.$emit("update:supplies", supplies);
        }
    }
});
